import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, CircleNotch, Sparkle, CheckCircle, WarningCircle, Gauge, Flag, Brain, Robot,
} from "@phosphor-icons/react";

const RISK = {
  low: { label: "Låg", color: "#16A34A" },
  medium: { label: "Medel", color: "#F59E0B" },
  high: { label: "Hög", color: "#EF4444" },
};

const scoreColor = (v) => (v == null ? "#a1a1aa" : v >= 80 ? "#16A34A" : v >= 60 ? "#F59E0B" : "#EF4444");

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-zinc-200 rounded-xl p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</span>
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
          <Icon size={18} weight="bold" />
        </div>
      </div>
      <div className="mt-3 font-display font-extrabold text-3xl tracking-tight">{value}</div>
    </div>
  );
}

const SCORE_META = [
  { key: "resolution", label: "Lösningsgrad" },
  { key: "tone", label: "Ton & empati" },
  { key: "accuracy", label: "Korrekthet" },
  { key: "policy", label: "Policyefterlevnad" },
];

export default function Quality() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/quality").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAnalysis = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post("/analyze/run");
      if (res.analyzed > 0) toast.success(`${res.analyzed} konversationer analyserade${res.remaining ? ` · ${res.remaining} kvar` : ""}`);
      else toast.info("Inga nya konversationer att analysera.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte köra analysen");
    } finally { setRunning(false); }
  };

  if (data === null) return <div className="p-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>;
  if (data === false) return <div className="p-8 text-zinc-500">Kunde inte ladda kvalitetsdata.</div>;

  const t = data.totals || {};
  const empty = (t.reviewed || 0) === 0;

  return (
    <div className="p-6 sm:p-8" data-testid="quality-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#0033FF]/10 flex items-center justify-center"><ShieldCheck size={22} weight="bold" className="text-[#0033FF]" /></div>
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Kvalitet (Auto-QA)</h1>
            <p className="text-zinc-500">AI granskar varje konversation — ton, lösningsgrad, korrekthet och policy. 100 % täckning, inga stickprov.</p>
          </div>
        </div>
        <button data-testid="run-analysis-button" onClick={runAnalysis} disabled={running}
          className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-full px-5 py-2.5 font-semibold text-sm disabled:opacity-50">
          {running ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
          {running ? "Analyserar…" : "Kör AI-analys"}
        </button>
      </div>

      {empty ? (
        <div className="mt-10 bg-white border border-zinc-200 rounded-xl py-16 text-center" data-testid="quality-empty">
          <div className="mx-auto h-12 w-12 rounded-xl bg-[#0033FF]/10 flex items-center justify-center"><Brain size={24} weight="duotone" className="text-[#0033FF]" /></div>
          <h3 className="mt-4 font-display font-bold text-lg">Ingen analys än</h3>
          <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">Klicka på "Kör AI-analys" så granskar AI:n dina konversationer och betygsätter kvaliteten.</p>
          {t.pending > 0 && <p className="mt-2 text-xs text-zinc-400">{t.pending} konversationer redo att analyseras.</p>}
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Gauge} label="Snittkvalitet" value={t.overall != null ? `${t.overall}` : "—"} accent={scoreColor(t.overall)} testid="quality-kpi-overall" />
            <Kpi icon={CheckCircle} label="Lösningsgrad" value={t.resolved_rate != null ? `${t.resolved_rate}%` : "—"} accent="#16A34A" testid="quality-kpi-resolved" />
            <Kpi icon={ShieldCheck} label="Granskade" value={t.reviewed} accent="#0033FF" testid="quality-kpi-reviewed" />
            <Kpi icon={WarningCircle} label="Väntar" value={t.pending} accent="#F59E0B" testid="quality-kpi-pending" />
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-6">
              <h3 className="font-display font-bold text-lg mb-5">Kvalitetsdimensioner</h3>
              <div className="space-y-4">
                {SCORE_META.map((s) => {
                  const v = data.avg_scores?.[s.key];
                  return (
                    <div key={s.key} data-testid={`quality-score-${s.key}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-zinc-700">{s.label}</span>
                        <span className="font-semibold" style={{ color: scoreColor(v) }}>{v != null ? v : "—"}</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${v || 0}%`, background: scoreColor(v) }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-6">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Brain size={18} weight="bold" className="text-violet-500" /> Hallucinationsrisk</h3>
              <div className="space-y-3">
                {["low", "medium", "high"].map((k) => {
                  const v = data.hallucination?.[k] || 0;
                  const total = (data.hallucination?.low || 0) + (data.hallucination?.medium || 0) + (data.hallucination?.high || 0);
                  const p = total ? Math.round((v / total) * 100) : 0;
                  return (
                    <div key={k} data-testid={`quality-risk-${k}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-2 font-medium text-zinc-700"><span className="h-2.5 w-2.5 rounded-full" style={{ background: RISK[k].color }} />{RISK[k].label}</span>
                        <span className="text-zinc-500 font-semibold">{v} · {p}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: RISK[k].color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {data.agents?.length > 0 && (
            <div className="mt-6 bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400 flex items-center gap-1.5"><Robot size={14} weight="bold" /> Per agent</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider border-b border-zinc-100">
                    <th className="py-3 px-4 font-semibold">Agent</th>
                    <th className="py-3 px-4 font-semibold text-right">Granskade</th>
                    <th className="py-3 px-4 font-semibold text-right">Lösningsgrad</th>
                    <th className="py-3 pl-4 px-4 font-semibold text-right">Snittkvalitet</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agents.map((a) => (
                    <tr key={a.id} className="border-b border-zinc-50 last:border-0" data-testid="quality-agent-row">
                      <td className="py-3 px-4 font-medium">{a.name}</td>
                      <td className="py-3 px-4 text-right">{a.count}</td>
                      <td className="py-3 px-4 text-right">{a.resolved_rate != null ? `${a.resolved_rate}%` : "—"}</td>
                      <td className="py-3 pl-4 px-4 text-right font-semibold" style={{ color: scoreColor(a.avg) }}>{a.avg != null ? a.avg : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6">
            <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Flag size={18} weight="fill" className="text-red-500" /> Flaggade konversationer</h3>
            {(!data.flagged || data.flagged.length === 0) ? (
              <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-sm text-zinc-500" data-testid="quality-flagged-empty">
                <CheckCircle size={28} weight="duotone" className="mx-auto mb-2 text-green-500/50" />
                Inga flaggade konversationer — kvaliteten ser bra ut!
              </div>
            ) : (
              <div className="space-y-3">
                {data.flagged.map((f, i) => (
                  <div key={i} data-testid="quality-flagged-row" className="bg-white border border-zinc-200 rounded-xl p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${scoreColor(f.overall)}1A`, color: scoreColor(f.overall) }}>{f.overall != null ? `${f.overall}/100` : "—"}</span>
                        {f.topic && <span className="text-xs font-semibold text-zinc-500">{f.topic}</span>}
                        <span className="text-xs text-zinc-400">· {f.agent_name}</span>
                        {f.hallucination_risk === "high" && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 inline-flex items-center gap-1"><Brain size={11} weight="fill" /> Hög hallucinationsrisk</span>}
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${f.resolved ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>{f.resolved ? "Löst" : "Olöst"}</span>
                    </div>
                    {f.summary && <p className="text-sm text-zinc-700 mt-2">{f.summary}</p>}
                    {f.flags?.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {f.flags.map((fl, k) => (
                          <li key={k} className="text-xs text-red-600 flex items-start gap-1.5"><Flag size={12} weight="fill" className="mt-0.5 shrink-0" /> {fl}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
